import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';

import { MembershipType, PersonOrgFinancialProfile, PersonOrgProfile, PersonOrgType } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { getPersonName, getPersonOrgName, useHouseholdRouteType } from 'utils';
import { useOrgLinks } from 'views/person/person-queries';
import { CondensedTable, HorizontalLayout, MailTo, ModalText, Text, TextLink, VerticalLayout } from 'widgets';

import { JointMemberDrawer } from './JointMemberDrawer';
import { MemberActionsCell, useMemberLinkActions } from './MemberLinkActions';

const columnHelper = createColumnHelper<PersonOrgFinancialProfile>();

const JointMemberContact = ({
    jointOrgGUID,
    membershipType,
    personProfiles,
}: {
    jointOrgGUID: string;
    membershipType: MembershipType;
    personProfiles: Map<string, PersonOrgProfile>;
}) => {
    const { data: orgLinks } = useOrgLinks(jointOrgGUID);
    const memberGuid = orgLinks?.find((link) => link.membershipType === membershipType)?.memberPersonOrgGUID;
    const memberProfile = memberGuid ? personProfiles.get(memberGuid) : undefined;
    const memberName = memberProfile ? getPersonName(memberProfile.personOrg) : '-';

    return (
        <VerticalLayout>
            {memberProfile && memberGuid ? (
                <TextLink to={`/person/${memberGuid}`}>{memberName}</TextLink>
            ) : (
                <Text $themeColor="subtle">{memberName}</Text>
            )}
            <MailTo address={memberProfile?.personOrg.primaryEmail?.plainText} />
        </VerticalLayout>
    );
};

export const JointMembersList = ({ profiles }: { profiles: PersonOrgFinancialProfile[] }) => {
    const { householdTypeLabel } = useHouseholdRouteType();
    const joints = useMemo(
        () => profiles.filter(({ profile }) => profile.personOrg.personOrgType === PersonOrgType.JOINT_PERSONS),
        [profiles],
    );
    const [showJointCreateDrawer, setShowJointCreateDrawer] = useState(false);
    const [jointToEdit, setJointToEdit] = useState<PersonOrgFinancialProfile | undefined>(undefined);
    const { openLinkDrawer, requestUnlink, linkUnlinkNodes } = useMemberLinkActions({
        personOrgType: PersonOrgType.JOINT_PERSONS,
        unlinkNoun: 'joint',
        getDisplayName: ({ profile }) => getPersonOrgName(profile.personOrg),
        unlinkNote: () => (
            <ModalText>Unlinking the joint does not unlink its member persons from the {householdTypeLabel}.</ModalText>
        ),
    });
    const personProfiles = useMemo(
        () =>
            new Map(
                profiles
                    .filter(({ profile }) => profile.personOrg.personOrgType === PersonOrgType.PERSON)
                    .map(({ profile }) => [profile.personOrgGUID, profile]),
            ),
        [profiles],
    );

    const columns = [
        columnHelper.accessor(({ profile }) => getPersonOrgName(profile.personOrg), {
            id: 'name',
            header: 'Name',
            cell: ({ getValue }) => <Text>{getValue()}</Text>,
            size: 1000,
        }),
        columnHelper.display({
            id: 'primary',
            header: 'Primary',
            cell: ({ row }) => (
                <JointMemberContact
                    jointOrgGUID={row.original.profile.personOrgGUID}
                    membershipType={MembershipType.JOINT_PRIMARY}
                    personProfiles={personProfiles}
                />
            ),
            size: 280,
        }),
        columnHelper.display({
            id: 'secondary',
            header: 'Secondary',
            cell: ({ row }) => (
                <JointMemberContact
                    jointOrgGUID={row.original.profile.personOrgGUID}
                    membershipType={MembershipType.JOINT_SECONDARY}
                    personProfiles={personProfiles}
                />
            ),
            size: 280,
        }),
        columnHelper.display({
            id: 'actions',
            header: '',
            cell: ({ row }) => (
                <MemberActionsCell profile={row.original} onEdit={setJointToEdit} onUnlink={requestUnlink} />
            ),
        }),
    ];

    return (
        <VerticalLayout $gap={8}>
            <HorizontalLayout $rowCenter $apart>
                <h2 className="text-cocoa-1000 text-xl">Joint Members</h2>
                <HorizontalLayout $gap={16}>
                    <Button onClick={openLinkDrawer}>Link Joint</Button>
                    <Button
                        onClick={() => {
                            setJointToEdit(undefined);
                            setShowJointCreateDrawer(true);
                        }}
                    >
                        New Joint
                    </Button>
                </HorizontalLayout>
            </HorizontalLayout>
            {joints.length > 0 ? (
                <Card>
                    <CondensedTable data={joints} columns={columns} onRowClick={setJointToEdit} />
                </Card>
            ) : (
                <Text $italic $themeColor="subtle">
                    There are no Joint Members in this {householdTypeLabel}
                </Text>
            )}
            {linkUnlinkNodes}
            {showJointCreateDrawer && <JointMemberDrawer onClose={() => setShowJointCreateDrawer(false)} />}
            {jointToEdit && (
                <JointMemberDrawer
                    onClose={() => setJointToEdit(undefined)}
                    orgGUID={jointToEdit.profile.personOrgGUID}
                />
            )}
        </VerticalLayout>
    );
};
